/**
 * memoryProbe — lightweight diagnostics for upload-related memory pressure.
 *
 * Admins reported full-tab Chrome crashes when uploading large videos. Those are
 * out-of-memory crashes, but the memory that blows up (video decode/media buffers)
 * lives OUTSIDE the JS heap, so `performance.memory.usedJSHeapSize` only tells part
 * of the story. The more meaningful signal is how many media decoders / preview
 * object URLs are alive at once — so this helper tracks those counters and logs them,
 * alongside JS heap size, at key upload lifecycle points.
 *
 * Usage:
 *   memoryProbe.decoderOpened()   // before creating a <video>/<audio> for metadata
 *   memoryProbe.decoderClosed()   // after revoking its object URL
 *   memoryProbe.objectUrlCreated()
 *   memoryProbe.objectUrlRevoked()
 *   memoryProbe.log('[AdminAssetUpload] queue add', { files: 12 })
 *
 * IMPORTANT: `performance.memory` is a non-standard, Chrome-only API. It is read
 * defensively and omitted where unavailable. Counters are the cross-browser signal.
 */

import { logger } from './loggerService';

let activeDecoders = 0;
let peakDecoders = 0;
let activeObjectUrls = 0;
let peakObjectUrls = 0;

function snapshotHeap() {
  // performance.memory: { usedJSHeapSize, totalJSHeapSize, jsHeapSizeLimit } (Chrome only)
  const mem = typeof performance !== 'undefined' && performance.memory;
  if (!mem) return null;
  const mb = (bytes) => Math.round(bytes / (1024 * 1024));
  return {
    usedHeapMB: mb(mem.usedJSHeapSize),
    totalHeapMB: mb(mem.totalJSHeapSize),
    heapLimitMB: mb(mem.jsHeapSizeLimit),
  };
}

export const memoryProbe = {
  decoderOpened() {
    activeDecoders += 1;
    if (activeDecoders > peakDecoders) peakDecoders = activeDecoders;
  },

  decoderClosed() {
    activeDecoders = Math.max(0, activeDecoders - 1);
  },

  objectUrlCreated() {
    activeObjectUrls += 1;
    if (activeObjectUrls > peakObjectUrls) peakObjectUrls = activeObjectUrls;
  },

  objectUrlRevoked() {
    activeObjectUrls = Math.max(0, activeObjectUrls - 1);
  },

  /**
   * Log a memory snapshot at a named lifecycle point.
   * @param {string} label - e.g. '[AdminAssetUpload] queue add'
   * @param {object} [context] - extra structured fields to include
   */
  log(label, context = {}) {
    logger.info(`[MemoryProbe] ${label}`, {
      activeDecoders,
      peakDecoders,
      activeObjectUrls,
      peakObjectUrls,
      ...snapshotHeap(),
      ...context,
    });
  },
};
